import { MapOrderMarker } from 'const/Map/MapOrderMarker';
import hull from 'hull.js';
import { flatten } from 'lodash';
import {
  EventData,
  GeoJSONSource,
  Map,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  Point,
  PointLike,
  Popup,
} from 'mapbox-gl';
import {
  geoJsonPropertiesToPlannerOrder,
  GeoOrders,
  getProperties,
} from 'pages/planner/map/utils/mapUtils';
import { PlannerOrder, WindowGroups } from 'pages/planner/utils/usePlannerData';
import React, { useEffect, useRef } from 'react';
import { BundleJson } from 'types/bundleTypes';
import { MapMarkerPopup } from '../MarkerPopup/MarkerPopup';
import './OrdersMarker.scss';

interface OrdersMarkerProps {
  map: Map;
  hoverPopupRef: Popup;
  rightClickPopupRef: Popup;
  orders: GeoOrders;
  selectedOrders: PlannerOrder[];
  selectedOrderIds: string[];
  windowGroups: WindowGroups;
  windows: [string, string][];
  fetchedBundles: BundleJson[];
  onSelect: (orders: PlannerOrder[]) => void;
}

export const OrdersMarker = ({
  map,
  hoverPopupRef,
  rightClickPopupRef,
  onSelect,
  ...props
}: OrdersMarkerProps) => {
  /* Canvas */
  const shiftSelectCanvasRef = useRef<HTMLElement>();
  /* Box visual while doing Shift+Drag */
  const shiftSelectAreaRef = useRef<HTMLElement>();
  /* Shift+select is activated status */
  const shiftSelectRef = useRef<boolean>(false);
  /* Variable to hold the starting xy coordinates when `mousedown` occured */
  const shiftSelectStartCoordinateRef = useRef<Point>();
  /* Variable to hold the current xy coordinates when `mousemove` or `mouseup` occurs */
  const shiftSelectCurrentCoordinateRef = useRef<Point>();

  /* assignLayers */
  /* Create the Layers for the Orders Marker */
  useEffect(() => {
    /* Wrapper */
    if (map.getSource(MapOrderMarker.SourceId)) {
      (map.getSource(MapOrderMarker.SourceId) as GeoJSONSource).setData(
        props.orders
      );
    } else {
      map.addSource(MapOrderMarker.SourceId, {
        type: 'geojson',
        data: props.orders,
        cluster: true,
        clusterMaxZoom: 10,
        clusterRadius: 25,
      });
    }

    /* Remove Existing Layers */
    map.getLayer(MapOrderMarker.ClusterId) &&
      map.removeLayer(MapOrderMarker.ClusterId);
    map.getLayer(MapOrderMarker.ClusterCountId) &&
      map.removeLayer(MapOrderMarker.ClusterCountId);
    map.getLayer(MapOrderMarker.PointId) &&
      map.removeLayer(MapOrderMarker.PointId);
    map.getLayer(MapOrderMarker.LabelId) &&
      map.removeLayer(MapOrderMarker.LabelId);

    /* Assign Layers */
    if (props.orders.features.length) {
      map.addLayer({
        id: MapOrderMarker.ClusterId,
        type: 'circle',
        source: MapOrderMarker.SourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            100,
            '#f1f075',
            750,
            '#f28cb1',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40,
          ],
        },
      });

      map.addLayer({
        id: MapOrderMarker.ClusterCountId,
        type: 'symbol',
        source: MapOrderMarker.SourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });

      map.addLayer({
        id: MapOrderMarker.PointId,
        type: 'circle',
        source: MapOrderMarker.SourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'window'],
            ...flatten(props.windows),
            '#661aff',
          ],
          'circle-radius': 10,
          'circle-stroke-width': 5,
          'circle-stroke-color': ['get', 'markerColor'],
        },
      });

      map.addLayer({
        id: MapOrderMarker.LabelId,
        type: 'symbol',
        source: MapOrderMarker.SourceId,
        filter: ['>', 'numberOfOrders', 1],
        layout: {
          'text-field': '{numberOfOrders}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });
    }
  }, [map, props.orders, props.windows]);

  /* onClusterClicked */
  useEffect(() => {
    /* Zoom in when the Cluster is clicked */
    const onClusterClickHandler = e => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [MapOrderMarker.ClusterId],
      });
      const clusterId = features[0].properties.cluster_id;
      (map.getSource(MapOrderMarker.SourceId) as any).getClusterExpansionZoom(
        clusterId,
        function (err, zoom) {
          if (err) return;

          map.easeTo({
            center: (features[0].geometry as any).coordinates,
            zoom: zoom,
          });
        }
      );
    };
    map.on('click', MapOrderMarker.ClusterId, onClusterClickHandler);

    return () => {
      map.off('click', MapOrderMarker.ClusterId, onClusterClickHandler);
    };
  }, [map]);

  /* hoverOnMarker */
  useEffect(() => {
    const onMouseEnter = e => {
      map.getCanvas().style.cursor = 'pointer';

      /* Add Popup */
      MapMarkerPopup(
        map,
        e.features,
        e.lngLat,
        hoverPopupRef,
        props.selectedOrderIds,
        props.windowGroups,
        props.fetchedBundles,
        onSelect,
        'Overview'
      );
    };
    map.on('mouseenter', MapOrderMarker.PointId, onMouseEnter);

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';

      /* Remove Popup */
      hoverPopupRef.remove();
    };
    map.on('mouseleave', MapOrderMarker.PointId, onMouseLeave);

    return () => {
      map.off('mouseenter', MapOrderMarker.PointId, onMouseEnter);
      map.off('mouseleave', MapOrderMarker.PointId, onMouseLeave);
    };
  }, [
    hoverPopupRef,
    map,
    onSelect,
    props.fetchedBundles,
    props.windowGroups,
    props.selectedOrderIds,
  ]);

  /* leftClickOnMarker */
  useEffect(() => {
    const orderSelection = (
      e: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      const newOrders = geoJsonPropertiesToPlannerOrder(
        e.features[0].properties
      );

      /* Only update selection if the selected marker does not have partial selection */
      if (!e.features[0].properties.partialSelection) {
        onSelect(newOrders);
      }
    };
    map.on('click', MapOrderMarker.PointId, orderSelection);

    return () => {
      map.off('click', MapOrderMarker.PointId, orderSelection);
    };
  }, [map, onSelect]);

  /* rightClickOnMarker */
  useEffect(() => {
    const action = (
      e: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      MapMarkerPopup(
        map,
        e.features,
        e.lngLat,
        rightClickPopupRef,
        props.selectedOrderIds,
        props.windowGroups,
        props.fetchedBundles,
        onSelect,
        'Edit'
      );
    };

    map.on('contextmenu', MapOrderMarker.PointId, action);

    return () => {
      map.off('contextmenu', MapOrderMarker.PointId, action);
    };
  }, [
    map,
    onSelect,
    props.fetchedBundles,
    props.windowGroups,
    rightClickPopupRef,
    props.selectedOrderIds,
  ]);

  /* shiftSelectOrders */
  useEffect(() => {
    shiftSelectCanvasRef.current = map.getCanvasContainer();

    /* Return the xy coordinates of the mouse position */
    const mousePos = (e: MouseEvent) => {
      const rect = shiftSelectCanvasRef.current.getBoundingClientRect();
      return new Point(
        e.clientX - rect.left - shiftSelectCanvasRef.current.clientLeft,
        e.clientY - rect.top - shiftSelectCanvasRef.current.clientTop
      );
    };

    const resetAllSelection = () => {
      shiftSelectStartCoordinateRef.current = undefined;
      shiftSelectCurrentCoordinateRef.current = undefined;

      /* Remove the Box visual for the Shift+Drag */
      shiftSelectAreaRef.current?.parentNode.removeChild(
        shiftSelectAreaRef.current
      );
      shiftSelectAreaRef.current = undefined;

      /* Remove the Shift+Select status */
      shiftSelectRef.current = false;
    };

    const action = (bbox?: PointLike | [PointLike, PointLike]) => {
      /* Do something */
      const features = map.queryRenderedFeatures(bbox, {
        layers: [MapOrderMarker.PointId],
      });

      if (features.length >= 1000) {
        return window.alert('Select a smaller number of features');
      }

      /* Run through the selected features */
      /* You will always get 2 exact same Features for every 1 Marker selected */
      const uniqueFeatures = features.reduce(
        (acc: MapboxGeoJSONFeature[], currentFeature: MapboxGeoJSONFeature) => {
          const isFeatureExist = acc.find(
            feature =>
              feature.properties.orders === currentFeature.properties.orders
          );
          if (!isFeatureExist) {
            acc.push(currentFeature);
          }

          return acc;
        },
        []
      );

      const ordersInShiftSelection = flatten(
        uniqueFeatures.reduce((acc, feature) => {
          acc.push(geoJsonPropertiesToPlannerOrder(feature.properties));
          return acc;
        }, [])
      );
      onSelect(ordersInShiftSelection);
    };

    const mouseMove = (
      e: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      /* Only run when there is Start Coordinate and the Shift+select is active */
      if (shiftSelectStartCoordinateRef.current && shiftSelectRef.current) {
        /* Set current coordinate */
        shiftSelectCurrentCoordinateRef.current = mousePos(e.originalEvent);

        /* Set the Box visual for the Shift+Drag */
        if (!shiftSelectAreaRef.current) {
          shiftSelectAreaRef.current = document.createElement('div');
          shiftSelectAreaRef.current.classList.add('boxdraw');
          shiftSelectCanvasRef.current.appendChild(shiftSelectAreaRef.current);
          shiftSelectCanvasRef.current.appendChild(shiftSelectAreaRef.current);
        }

        const minX = Math.min(
            shiftSelectStartCoordinateRef.current.x,
            shiftSelectCurrentCoordinateRef.current.x
          ),
          maxX = Math.max(
            shiftSelectStartCoordinateRef.current.x,
            shiftSelectCurrentCoordinateRef.current.x
          ),
          minY = Math.min(
            shiftSelectStartCoordinateRef.current.y,
            shiftSelectCurrentCoordinateRef.current.y
          ),
          maxY = Math.max(
            shiftSelectStartCoordinateRef.current.y,
            shiftSelectCurrentCoordinateRef.current.y
          );

        /* Adjust width and xy position of the box element ongoing */
        const pos = `translate(${minX}px, ${minY}px)`;
        shiftSelectAreaRef.current.style.transform = pos;
        shiftSelectAreaRef.current.style.webkitTransform = pos;
        shiftSelectAreaRef.current.style.width = `${maxX - minX}px`;
        shiftSelectAreaRef.current.style.height = `${maxY - minY}px`;
      }
    };

    const mouseUp = () => {
      /* Only run when the SHIFT is pressed and the area was selected */
      /* Selected area = (Start Coordinate + Current coordinate exist) */
      if (
        shiftSelectRef.current &&
        shiftSelectStartCoordinateRef.current &&
        shiftSelectCurrentCoordinateRef.current
      ) {
        action([
          shiftSelectStartCoordinateRef.current,
          shiftSelectCurrentCoordinateRef.current,
        ]);

        map.off('mousemove', mouseMove);
        map.off('mouseup', mouseUp);
      }

      /* Cleanup */
      resetAllSelection();
    };

    const mouseDown = (
      e: MapMouseEvent & { features?: MapboxGeoJSONFeature[] } & EventData
    ) => {
      shiftSelectRef.current = e.originalEvent.shiftKey;

      /* Only run when the SHIFT key is pressed */
      if (shiftSelectRef.current) {
        /* Set the starting coordinate */
        shiftSelectStartCoordinateRef.current = mousePos(e.originalEvent);

        map.on('mousemove', mouseMove);
        map.on('mouseup', mouseUp);
      }
    };

    map.on('mousedown', mouseDown);

    return () => {
      map.off('mousedown', mouseDown);
      map.off('mousemove', mouseMove);
      map.off('mouseup', mouseUp);
    };
  }, [map, onSelect]);

  /* changeMousePointerStyleOnClusterHover */
  /* Change the MoousePointer style when hover on the Cluster */
  useEffect(() => {
    const onMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    map.on('mouseenter', MapOrderMarker.ClusterId, onMouseEnter);

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };
    map.on('mouseleave', MapOrderMarker.ClusterId, onMouseLeave);

    return () => {
      map.off('mouseenter', MapOrderMarker.ClusterId, onMouseEnter);
      map.off('mouseleave', MapOrderMarker.ClusterId, onMouseLeave);
    };
  }, [map]);

  /* showPolygonOnClusterHover */
  /* Show Polygon when hover on the Cluster */
  useEffect(() => {
    const onMouseEnter = e => {
      const properties = getProperties<{
        cluster_id: number;
        point_count: number;
      }>(map, MapOrderMarker.ClusterId, e);
      const { cluster_id, point_count } = properties;
      const clusterSource = map.getSource(
        MapOrderMarker.SourceId
      ) as GeoJSONSource;

      clusterSource.getClusterLeaves(
        cluster_id,
        point_count,
        0,
        function (err, aFeatures) {
          const coordinates = aFeatures
            ?.map(f => {
              if (f.geometry.type === 'Point') {
                return f.geometry.coordinates;
              }
              return null;
            })
            .filter(Boolean);

          if (!err && !map.getSource(MapOrderMarker.ClusterPolygonSourceId)) {
            map.addSource(MapOrderMarker.ClusterPolygonSourceId, {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [hull(coordinates, 20)],
                },
              } as any,
            });

            map.addLayer({
              id: MapOrderMarker.ClusterPolygonLayerId,
              type: 'fill',
              source: MapOrderMarker.ClusterPolygonSourceId,
              layout: {},
              paint: {
                'fill-color': '#661aff',
                'fill-opacity': 0.5,
              },
            });
            map.addLayer({
              id: MapOrderMarker.ClusterPolygonOutlineId,
              type: 'line',
              source: MapOrderMarker.ClusterPolygonSourceId,
              layout: {},
              paint: {
                'line-color': '#000',
                'line-width': 1,
              },
            });

            // I want the cluster hover layer to be below the cluster layer/marker
            if (map.getLayer(MapOrderMarker.ClusterId)) {
              map.moveLayer(
                MapOrderMarker.ClusterPolygonOutlineId,
                MapOrderMarker.ClusterId
              );
              map.moveLayer(
                MapOrderMarker.ClusterPolygonLayerId,
                MapOrderMarker.ClusterPolygonOutlineId
              );
            }
          }
        }
      );
    };
    map.on('mouseenter', MapOrderMarker.ClusterId, onMouseEnter);

    const onMouseLeave = function () {
      if (map.getLayer(MapOrderMarker.ClusterPolygonOutlineId)) {
        map.removeLayer(MapOrderMarker.ClusterPolygonOutlineId);
      }
      if (map.getLayer(MapOrderMarker.ClusterPolygonLayerId)) {
        map.removeLayer(MapOrderMarker.ClusterPolygonLayerId);
      }
      if (map.getSource(MapOrderMarker.ClusterPolygonSourceId)) {
        map.removeSource(MapOrderMarker.ClusterPolygonSourceId);
      }
    };
    map.on('mouseleave', MapOrderMarker.ClusterId, onMouseLeave);

    return () => {
      map.off('mouseenter', MapOrderMarker.ClusterId, onMouseEnter);
      map.off('mouseleave', MapOrderMarker.ClusterId, onMouseLeave);
    };
  }, [map]);

  return <></>;
};
